'use client'

import * as React from 'react'

import { Spinner } from '@src/components/Spinner/Spinner'
import { useClickAction } from '@src/hooks/useClickAction/useClickAction'
import { type UseClickActionArgs } from '@src/hooks/useClickAction/useClickActionArgs'
import { filterBuilderAttributes, filterOutBuilderProps } from '@src/utils/builder.util'
import { cn } from '@src/utils/tailwind'

export const ButtonVariants = ['primary', 'secondary', 'dark', 'black'] as const
export const ButtonSizes = ['small', 'medium', 'large', 'massive'] as const

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  UseClickActionArgs & {
    variant?: (typeof ButtonVariants)[number]
    size?: (typeof ButtonSizes)[number]
    children: React.ReactNode | React.ReactNode[]
    isLoading?: boolean
    loadingComponent?: React.ReactNode
    attributes?: any
    contentClassName?: string
  }

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>((allProps, ref) => {
  const {
    children,
    size = 'large',
    variant = 'primary',
    isLoading: _isLoading,
    loadingComponent,
    className,
    contentClassName,
    attributes,
    ...buttonProps
  } = allProps
  // separate unused builder props
  const props = filterOutBuilderProps(buttonProps)
  const {
    ClickWrapper,
    handlers,
    isLoading: isClickActionLoading,
    isWrapping,
  } = useClickAction(allProps)
  const isLoading = _isLoading || isClickActionLoading // eslint-disable-line @typescript-eslint/prefer-nullish-coalescing
  const usesLoading = isLoading !== undefined

  return (
    <ClickWrapper {...(isWrapping && filterBuilderAttributes(attributes))}>
      <button
        ref={ref}
        {...props}
        {...(!isWrapping && filterBuilderAttributes(attributes))}
        {...(!isWrapping && handlers)}
        className={cn(
          `relative rounded-full border border-solid text-body-lg font-semibold text-bodi-black

          transition-all after:absolute after:inset-0 after:hidden after:w-full after:rounded-full after:border-solid after:border-white after:mix-blend-difference

          focus-visible:after:block

          active:ring-transparent
          `,
          {
            'border-transparent bg-bodi-green-bright-500 hover:bg-bodi-green-bright-600 active:bg-bodi-green-bright-600':
              variant === 'primary',
            'border-bodi-gray-medium bg-transparent hover:bg-black/5 active:bg-black/5 text-bodi-black-text':
              variant === 'secondary',
            'border-transparent bg-bodi-gray-medium text-bodi-white hover:bg-bodi-gray-dark active:bg-bodi-gray-dark':
              variant === 'dark',
            'border-transparent bg-bodi-black-text text-bodi-white hover:bg-neutral-800 active:bg-neutral-800':
              variant === 'black',
            'h-[28px]': size === 'small',
            'h-[44px]': size === 'medium',
            'h-[60px]': size === 'large',
            'h-[64px]': size === 'massive',
          },
          isLoading && {
            'cursor-not-allowed opacity-80': isLoading,
            'hover:bg-bodi-green-bright-500 active:bg-bodi-green-bright-500': variant === 'primary',
          },
          className,
          attributes?.className
        )}
      >
        <div
          className={cn(
            'relative flex size-full items-center justify-center gap-3 whitespace-nowrap',
            {
              'space-x-1 px-8 text-body-sm': size === 'small',
              'space-x-2 px-8 text-body-md': size === 'medium',
              'space-x-3 px-8 text-body-md': size === 'large',
              // eslint-disable-next-line no-restricted-syntax
              'space-x-3 px-8 text-3xl font-bold leading-7 tracking-[1px]': size === 'massive',
            },
            usesLoading && {
              'px-10': size === 'small' || size === 'medium',
              'px-12': size === 'large' || size === 'massive',
            },
            'leading-3 tracking-wider',
            contentClassName
          )}
        >
          {children}
        </div>
        {isLoading && (
          <div
            className={cn('absolute inset-y-0 right-2 flex items-center', {
              'right-3': size === 'large',
            })}
          >
            {loadingComponent ?? <Spinner size={size} />}
          </div>
        )}
      </button>
    </ClickWrapper>
  )
})

Button.displayName = 'Button'
